import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { LayoutDashboard, CalendarDays, Settings, LogOut, Link2, Users, Wallet, Menu, X, Crown, Lock, Sun, Moon, Monitor, Globe, FolderOpen, FileUp, HelpCircle, Mail, Send, ListChecks, MessageCircle, Bell, BarChart3, ChevronsUpDown, Bot } from 'lucide-react';
import { BookingLinksDialog } from '@/components/BookingLinksDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useTheme } from '@/components/ThemeProvider';
import api from '@/api';

import { useIndustry } from '@/components/IndustryContext';

const requestNotificationPermission = () => {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
};

const showBrowserNotification = (title, body) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const n = new Notification(title, {
        body,
        icon: '/logo.png',
        tag: 'propraxis-pending',
        renotify: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
      setTimeout(() => n.close(), 8000);
    } catch {}
  }
};

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useIndustry();
  const [linksOpen, setLinksOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [plan, setPlan] = useState('start');
  const [role, setRole] = useState('doctor');
  const [userName, setUserName] = useState('');
  const [permissions, setPermissions] = useState({});
  const [features, setFeatures] = useState([]);
  const { theme, setTheme } = useTheme();
  const [badges, setBadges] = useState({ pending_appointments: 0, unread_chats: 0 });
  const prevBadgesRef = useRef(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => localStorage.getItem('pp_notifications') !== 'off'
  );

  const cycleTheme = () => {
    const next = theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark';
    setTheme(next);
  };
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;

  const toggleNotifications = useCallback(() => {
    const next = !notificationsEnabled;
    setNotificationsEnabled(next);
    localStorage.setItem('pp_notifications', next ? 'on' : 'off');
    if (next) requestNotificationPermission();
  }, [notificationsEnabled]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const token = document.cookie.includes('pp_csrf');
    if (token) {
      api.get('/auth/me')
        .then(({ data }) => {
          setRole(data.role || 'doctor');
          setPermissions(data.permissions || {});
          setFeatures(data.active_features || []);
        })
        .catch(() => {});
      api.get('/subscription')
        .then(({ data }) => setPlan(data.plan?.id || 'start'))
        .catch(() => {});
      api.get('/settings')
        .then(({ data }) => setUserName(data?.psychologist_name || ''))
        .catch(() => {});
      if (notificationsEnabled) requestNotificationPermission();
      // Badge polling with notification detection
      const fetchBadges = () => {
        if (document.hidden) return;  // don't poll while the tab is in the background
        api.get('/badge-counts').then(({ data }) => {
        setBadges(prev => {
          const prevCount = prevBadgesRef.current;
          // Only notify if count increased (not on first load)
          if (prevCount !== null && notificationsEnabled) {
            const newPending = data.pending_appointments - (prevCount.pending_appointments || 0);
            const newChats = data.unread_chats - (prevCount.unread_chats || 0);
            if (newPending > 0) {
              showBrowserNotification(
                'Новая заявка',
                `${newPending} ${newPending === 1 ? 'новая запись ожидает' : 'новых записей ожидают'} подтверждения`
              );
            } else if (newChats > 0) {
              showBrowserNotification(
                'Новое сообщение',
                `${newChats} ${newChats === 1 ? 'непрочитанное сообщение' : 'непрочитанных сообщений'}`
              );
            }
          }
          prevBadgesRef.current = data;
          return data;
        });
      }).catch(() => {});
      };
      fetchBadges();
      const badgeInterval = setInterval(fetchBadges, 15000);
      // Refresh immediately when the tab is brought back to the foreground
      const onVisible = () => { if (!document.hidden) fetchBadges(); };
      document.addEventListener('visibilitychange', onVisible);
      return () => { clearInterval(badgeInterval); document.removeEventListener('visibilitychange', onVisible); };
    }
  }, [notificationsEnabled]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    navigate('/login');
  };

  const nav = (path) => { navigate(path); setMobileOpen(false); };
  const isDoctor = role === 'doctor' || role === 'owner';
  const isOwner = role === 'owner';
  const roleLabel = isOwner ? 'Владелец' : !isDoctor ? 'Администратор' : 'Специалист';
  const hasFeature = (f) => features.includes(f);

  // Build navigation dynamically based on role + permissions + features
  const buildNav = () => {
    if (isDoctor) {
      const items = [
        { icon: LayoutDashboard, label: 'Обзор', path: '/dashboard' },
        { icon: Bot, label: 'Ассистент', path: '/assistant' },
      ];
      // Owner doesn't need queue, appointments, booking links
      if (!isOwner) {
        items.push({ icon: ListChecks, label: 'Живая очередь', path: '/queue' });
        items.push({ icon: CalendarDays, label: 'Записи', path: '/appointments', badge: badges.pending_appointments });
        if (hasFeature('secure_chat')) {
          items.push({ icon: MessageCircle, label: 'Сообщения', path: '/messenger', badge: badges.unread_chats });
        }
      }
      items.push(
        { icon: Users, label: t('clients') || 'Клиенты', path: '/patients' },
        { icon: FolderOpen, label: 'Файлы', path: '/files', requiresPlan: 'practic' },
        { icon: Wallet, label: 'Финансы', path: '/finance', requiresPlan: 'practic' },
      );
      // Owner gets "Сайт клиники" instead of "Визитка"
      if (!isOwner) {
        items.push({ icon: Globe, label: 'Визитка', path: '/profile-edit' });
      }
      if (isOwner) {
        items.push({ icon: BarChart3, label: 'Аналитика', path: '/analytics' });
      }
      items.push({ icon: Settings, label: 'Настройки', path: '/settings' });
      return items;
    }
    // Admin — dynamic based on permissions + features
    const items = [
      { icon: LayoutDashboard, label: 'Обзор', path: '/dashboard' },
      { icon: Bot, label: 'Ассистент', path: '/assistant' },
    ];
    if (permissions.can_manage_calendar) {
      items.push({ icon: ListChecks, label: 'Живая очередь', path: '/queue' });
      items.push({ icon: CalendarDays, label: 'Записи', path: '/appointments', badge: badges.pending_appointments });
      if (hasFeature('secure_chat')) {
        items.push({ icon: MessageCircle, label: 'Сообщения', path: '/messenger', badge: badges.unread_chats });
      }
    }
    if (permissions.can_manage_patients) {
      items.push({ icon: Users, label: 'Клиенты', path: '/patients' });
    }
    items.push({ icon: FolderOpen, label: 'Файлы', path: '/files' });
    if (permissions.can_manage_finances) {
      items.push({ icon: Wallet, label: 'Финансы', path: '/finance' });
    }
    items.push({ icon: Settings, label: 'Настройки', path: '/settings' });
    return items;
  };

  const navItems = buildNav();

  const sidebarContent = (
    <>
      <div className="p-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-0.5 cursor-pointer" onClick={() => nav('/')}>
          <img src="/logo.png" alt="ProPraxis" className="w-12 h-12 rounded-md object-cover" />
          <div>
            <h1 className="font-heading text-base text-stone-200 tracking-tight leading-tight">
              <span className="text-stone-400">Pro</span><span className="text-amber-500">Praxis</span>
            </h1>
            {!isDoctor && (
              <span className="text-[10px] text-amber-600 font-medium uppercase tracking-wider">Администратор</span>
            )}
            {isOwner && (
              <span className="text-[10px] text-teal-500 font-medium uppercase tracking-wider">Владелец</span>
            )}
          </div>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden text-stone-500 hover:text-stone-200 p-1">
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 px-3 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const locked = item.requiresPlan && !['practic', 'profi', 'clinic'].includes(plan);
          return (
            <button key={item.path} data-testid={`nav-${item.path.slice(1)}`}
              onClick={() => locked ? nav('/pricing') : nav(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                isActive ? 'bg-amber-700/15 text-amber-500 font-medium' :
                locked ? 'text-stone-700 hover:text-stone-500 hover:bg-stone-900' :
                'text-stone-500 hover:text-stone-300 hover:bg-stone-900'
              }`}>
              <item.icon size={18} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge > 0 && (
                <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{item.badge > 99 ? '99+' : item.badge}</span>
              )}
              {locked && <Lock size={13} className="text-stone-700" />}
            </button>
          );
        })}
        <div className="pt-4 space-y-1">
          {(isDoctor || role === 'admin') && !isOwner && (
            <>
              <button data-testid="copy-booking-link" onClick={() => { setLinksOpen(true); setMobileOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-stone-500 hover:text-amber-500 hover:bg-stone-900 transition-all duration-200">
                <Link2 size={18} />
                <span>Ссылка записи</span>
              </button>
              <button onClick={() => { nav('/import'); }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-stone-500 hover:text-amber-500 hover:bg-stone-900 transition-all duration-200">
                <FileUp size={18} />
                <span>Импорт данных</span>
              </button>
            </>
          )}
          <button data-testid="theme-toggle" onClick={cycleTheme}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-stone-500 hover:text-amber-500 hover:bg-stone-900 transition-all duration-200">
            <ThemeIcon size={18} />
            <span>{theme === 'dark' ? 'Тёмная' : theme === 'light' ? 'Светлая' : 'Системная'}</span>
          </button>
          <button data-testid="notifications-toggle" onClick={toggleNotifications}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all duration-200 ${
              notificationsEnabled ? 'text-amber-500 hover:text-amber-400 hover:bg-stone-900' : 'text-stone-600 hover:text-stone-400 hover:bg-stone-900'
            }`}>
            <Bell size={18} />
            <span>Уведомления</span>
            <span className={`ml-auto text-[10px] font-medium ${notificationsEnabled ? 'text-green-500' : 'text-stone-700'}`}>
              {notificationsEnabled ? 'Вкл' : 'Выкл'}
            </span>
          </button>
          <button data-testid="sidebar-support-btn" onClick={() => { setSupportOpen(true); setMobileOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-stone-500 hover:text-amber-500 hover:bg-stone-900 transition-all duration-200">
            <HelpCircle size={18} />
            <span>Поддержка</span>
          </button>
        </div>
      </nav>

      <div className="p-3 space-y-2 border-t border-stone-800/60">
        {/* Account identity + quick menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="account-menu-btn" aria-label="Меню аккаунта"
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-stone-900 transition-colors text-left">
              <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-heading text-white" style={{ background: 'var(--app-accent)' }}>
                {(userName || 'P').trim().charAt(0).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium truncate" style={{ color: 'var(--app-text)' }}>{userName || 'Аккаунт'}</span>
                <span className="block text-[11px] truncate" style={{ color: 'var(--app-text-muted)' }}>{roleLabel}</span>
              </span>
              <ChevronsUpDown size={14} style={{ color: 'var(--app-text-muted)' }} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56">
            {!isOwner && (
              <DropdownMenuItem data-testid="account-menu-profile" onClick={() => nav('/profile-edit')}>
                <Globe size={14} className="mr-2" /> Визитка
              </DropdownMenuItem>
            )}
            {isDoctor && (
              <DropdownMenuItem data-testid="account-menu-billing" onClick={() => nav('/pricing')}>
                <Crown size={14} className="mr-2" /> Тарифы и подписка
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => nav('/settings')}>
              <Settings size={14} className="mr-2" /> Настройки
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {isDoctor && (
          <button onClick={() => nav('/pricing')}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${
              plan === 'profi' ? 'bg-violet-900/20 text-violet-400' :
              plan === 'practic' ? 'bg-amber-900/20 text-amber-500' :
              'bg-stone-800/50 text-stone-500 hover:bg-stone-800'
            }`}>
            <Crown size={14} />
            <span>{plan === 'clinic' ? 'Организация' : plan === 'profi' ? 'Профи' : plan === 'practic' ? 'Практик' : 'Старт'}</span>
            {plan === 'start' && <span className="ml-auto text-amber-600 text-[10px]">Улучшить</span>}
          </button>
        )}
        <button data-testid="logout-btn" onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-stone-600 hover:text-red-400 hover:bg-stone-900/60 transition-all duration-200">
          <LogOut size={18} />
          <span>Выход</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center gap-0.5 px-4 py-2.5 border-b border-stone-800/80" style={{ background: 'var(--app-sidebar)' }}>
        <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="text-stone-400 hover:text-stone-200 p-1">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-0.5 cursor-pointer" onClick={() => nav('/')}>
          <img src="/logo.png" alt="ProPraxis" className="w-11 h-11 rounded-md object-cover" />
          <span className="font-heading text-stone-200 text-sm"><span className="text-stone-400">Pro</span><span className="text-amber-500">Praxis</span></span>
        </div>
      </div>
      <aside data-testid="sidebar" className="hidden lg:flex w-64 h-screen flex-col border-r border-stone-800/80 shrink-0 sticky top-0" style={{ background: 'var(--app-sidebar)' }}>
        {sidebarContent}
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col animate-slide-in" style={{ background: 'var(--app-sidebar)' }}>
            {sidebarContent}
          </aside>
        </div>
      )}
      {(isDoctor || role === 'admin') && <BookingLinksDialog open={linksOpen} onOpenChange={setLinksOpen} />}
      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <div className="p-6 space-y-5">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center bg-amber-900/20">
                <HelpCircle size={28} className="text-amber-500" />
              </div>
              <h3 className="font-heading text-lg mb-1 text-stone-200">Поддержка ProPraxis</h3>
              <p className="text-xs text-stone-500">Свяжитесь с нами любым удобным способом</p>
            </div>
            <div className="space-y-3">
              <a data-testid="sidebar-support-email" href="mailto:support@propraxis.ru"
                className="flex items-center gap-3 p-3 rounded-lg border border-stone-800 bg-stone-950/40 transition-colors hover:border-amber-700/50">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-amber-900/20">
                  <Mail size={18} className="text-amber-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-200">Почта</p>
                  <p className="text-xs text-amber-500">support@propraxis.ru</p>
                </div>
              </a>
              <a data-testid="sidebar-support-telegram" href="https://t.me/elbulm" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-stone-800 bg-stone-950/40 transition-colors hover:border-sky-700/50">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-sky-900/30">
                  <Send size={18} className="text-sky-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-200">Telegram</p>
                  <p className="text-xs text-sky-400">@elbulm</p>
                </div>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
